import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, X } from "lucide-react";
import myndaLogo from "assets/images/mynda-logo.png";
import { useIsMobile } from "hooks/useMediaQuery";
import { isActivePath, NAV_SECTIONS } from "./navItems";

interface Props {
  open: boolean;
  onClose: () => void;
}

const STORAGE_KEY = "mynda-admin:nav-collapsed";

/** Sections the operator has folded away, remembered between visits. */
const readCollapsed = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

/**
 * The main navigation: a persistent rail on desktop, a drawer on a phone.
 *
 * The thirty links are grouped (see `navItems.ts`) because scrolling a flat
 * list of thirty to change page is the single most repeated action for staff
 * working from a phone. The section holding the current page is always open,
 * so you can never fold away the thing you are looking at.
 *
 * Below `md` this is a modal dialog and behaves like one — Escape closes it,
 * the page behind it does not scroll, focus is trapped inside and returns to
 * the trigger on close. Above `md` it is persistent chrome, so it takes none of
 * those behaviours and no dialog role.
 */
export default function AppSidebar({ open, onClose }: Props) {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const asideRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);

  const isModal = isMobile && open;

  const toggleSection = (title: string) =>
    setCollapsed((current) => {
      const next = current.includes(title)
        ? current.filter((entry) => entry !== title)
        : [...current, title];
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // A private-mode storage failure should not break navigation.
      }
      return next;
    });

  // Escape closes, and focus moves in on open and back to the opener on close.
  useEffect(() => {
    if (!isModal) return;

    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !asideRef.current) return;

      // Trap Tab inside the drawer; without this it walks the page underneath,
      // which is covered by a backdrop and cannot be seen.
      const focusable = asideRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      opener?.focus?.();
    };
  }, [isModal, onClose]);

  // Stop the page behind the drawer from scrolling under the operator's thumb.
  useEffect(() => {
    if (!isModal) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isModal]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        ref={asideRef}
        role={isModal ? "dialog" : undefined}
        aria-modal={isModal ? true : undefined}
        aria-label="Main navigation"
        className={`fixed top-0 left-0 h-full w-64 bg-slate-900 text-white z-30 flex flex-col transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        {/*
          The brand mark sits on white rather than on the sidebar's slate: the
          logo's wordmark is black, so on a dark ground it simply disappears.
          There is a light-wordmark variant of the square lockup in the other
          repos, but none of the horizontal one, and recolouring a brand asset
          is not something to do unilaterally — a white bar shows it exactly as
          designed, and reads as continuous with the white header beside it.
        */}
        <div className="flex items-center justify-between px-5 h-16 bg-white flex-shrink-0">
          <Link to="/dashboard" onClick={onClose} aria-label="Mynda — dashboard">
            <img
              src={myndaLogo}
              alt="Mynda"
              className="h-8 w-auto object-contain"
            />
          </Link>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Close navigation"
            data-touch="icon"
            className="md:hidden text-slate-500 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 scrollbar-thin scrollbar-thumb-slate-700">
          {NAV_SECTIONS.map(({ title, items }) => {
            // Never fold away the section holding the current page.
            const holdsActive = items.some((item) =>
              isActivePath(item.path, pathname)
            );
            const isOpen = holdsActive || !collapsed.includes(title);

            return (
              <div key={title} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleSection(title)}
                  aria-expanded={isOpen}
                  data-compact
                  className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {title}
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${isOpen ? "" : "-rotate-90"}`}
                  />
                </button>

                {isOpen && (
                  <div className="space-y-0.5">
                    {items.map(({ label, path, icon: Icon }) => {
                      const active = isActivePath(path, pathname);
                      return (
                        <Link
                          key={path}
                          to={path}
                          onClick={onClose}
                          aria-current={active ? "page" : undefined}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
                            ${
                              active
                                ? "bg-indigo-600 text-white"
                                : "text-slate-300 hover:bg-slate-800 hover:text-white"
                            }`}
                        >
                          <Icon size={16} className="flex-shrink-0" />
                          {label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t border-slate-700 text-xs text-slate-500">
          Mynda Operations v2.0
        </div>
      </aside>
    </>
  );
}
